import { List } from 'antd';
import { useTheme } from 'styled-components';
import useTestListPageController from '../../1_application/test/testListPageController';
import ITestRepository from '../../2_domain/test/iTestRepository';
import { TestStatusDto, TestSummary } from '../../2_domain/test/test';
import withAsync from '../../lib/asyncBounrdary/withAsync';
import wrapPromise from '../../lib/asyncBounrdary/wrapPromise';
import Get from '../../lib/di/get';
import { getIndex } from '../../lib/util/pagination';
import { SearchControlBoxContainer } from '../core/components/box/boxContainer';
import Column from '../core/components/box/column';
import Row from '../core/components/box/row';
import SizedBox from '../core/components/box/sizedBox';
import LXMiddleButton from '../core/components/buttons/lxMiddleButton';
import LXPageSizeSelector from '../core/components/buttons/lxPageSizeSelector';
import LXRefreshButton from '../core/components/buttons/lxRefreshButton';
import LXSearchButton from '../core/components/buttons/lxSearchButton';
import LXSelector from '../core/components/buttons/lxSelector';
import LXSortSelector from '../core/components/buttons/lxSortSelector';
import { XCenter } from '../core/components/center';
import LXList from '../core/components/list/lx_list';
import {
  HeaderPadding,
  ItemPadding,
} from '../core/components/list/lx_list_padding';
import LXError from '../core/error/lx_error';
import LXPagination from '../core/pagination/lxPagination';
import LXSpinner from '../core/spinner/lx_spinner';
import LXDateInput from '../core/texts/lxDateInput';
import LXSearcher from '../core/texts/lxSearcher';
import { LXbody, LXCaption, LXh2, LXh5 } from '../core/texts/lxText';

const TestListPage = ({ testStatuses }: { testStatuses: TestStatusDto[] }) => {
  const theme = useTheme();
  const c = useTestListPageController(testStatuses);
  const header = (
    <Row>
      <LXh2>{theme.texts.testList}</LXh2>
      <SizedBox width={8}></SizedBox>
      <LXh2 fontWeight={400} color={theme.colors.caption}>
        {c.pageResult?.totalElements ?? 0}
      </LXh2>
    </Row>
  );

  const statusSelector = (
    <LXSelector<TestStatus | 'all'>
      value={c.selectedStatus}
      values={['all'].concat(testStatuses.map((val) => val.status))}
      labels={['All'].concat(testStatuses.map((val) => val.label))}
      onSelect={c.onSelect}
      dropdownItemWidth={240}
      dropdownHeaderWidth={240}
    ></LXSelector>
  );

  const searchBox = (
    <SearchControlBoxContainer>
      <Column>
        <Row alignItems="end">
          <LXDateInput
            base={c.beginDate}
            label={theme.texts.from}
            widths={[80, 80, 96]}
            onDateChange={c.setBeginDate}
          ></LXDateInput>
          <SizedBox width={8}></SizedBox>
          <Column>
            <LXh5>~</LXh5>
            <SizedBox height={16}></SizedBox>
          </Column>
          <SizedBox width={8}></SizedBox>
          <LXDateInput
            base={c.endDate}
            label={theme.texts.to}
            widths={[80, 80, 96]}
            onDateChange={c.setEndDate}
          ></LXDateInput>
          <SizedBox width={16}></SizedBox>
          {statusSelector}
        </Row>
        <SizedBox height={24}></SizedBox>
        <Row justifyContent="space-between" alignItems="center">
          <LXSearcher
            value={c.searchText}
            onChange={c.setSearchText}
            placeholder={theme.texts.keywordSearch}
          ></LXSearcher>
          <Row>
            <LXSearchButton onClick={c.onSearch}></LXSearchButton>
            <SizedBox width={8}></SizedBox>
            <LXRefreshButton onClick={c.onRefresh}></LXRefreshButton>
          </Row>
        </Row>
      </Column>
    </SearchControlBoxContainer>
  );

  const pagination = (
    <Row justifyContent="space-between" alignItems="center">
      <Row width="192px">
        <LXMiddleButton onClick={c.onNewTestHandler}>
          {theme.texts.plusAddNew}
        </LXMiddleButton>
      </Row>
      <LXPagination
        current={c.pageResult?.pageNumber ?? 0}
        pageSize={c.pagination.pageSize}
        total={c.pageResult?.totalElements ?? 0}
        onChange={c.setPageNumber}
      ></LXPagination>
      <Row alignItems="center">
        <LXbody color={theme.colors.caption}>{theme.texts.rowsPerPage}</LXbody>
        <SizedBox width={10}></SizedBox>
        <LXPageSizeSelector
          value={c.pagination.pageSize}
          onSelect={c.setPageSize}
        ></LXPageSizeSelector>
      </Row>
    </Row>
  );

  const buildTestName = (test: TestSummary) => {
    if (test.numberOfItems > 1) {
      return (
        <Row>
          <LXbody>{test.name}</LXbody>
          <SizedBox width={2}></SizedBox>
          <LXbody color={theme.colors.caption}>{`+${
            test.numberOfItems - 1
          }`}</LXbody>
        </Row>
      );
    } else {
      return <LXbody>{test.name}</LXbody>;
    }
  };

  const listHeader = (
    <HeaderPadding>
      <Row alignItems="center">
        <LXCaption width={'64px'}>{theme.texts.no}</LXCaption>
        <SizedBox width={16}></SizedBox>
        <LXCaption width={'120px'}>{theme.texts.testNo}</LXCaption>
        <SizedBox width={16}></SizedBox>
        <LXCaption width={'312px'}>{theme.texts.testName}</LXCaption>
        <SizedBox width={16}></SizedBox>
        <LXCaption width={'176px'}>{theme.texts.clientName}</LXCaption>
        <SizedBox width={16}></SizedBox>
        <LXCaption width={'144px'}>{theme.texts.status}</LXCaption>
      </Row>
    </HeaderPadding>
  );

  const listItemBuilder = (test: TestSummary, idx: number) => (
    <List.Item>
      <ItemPadding onClick={() => c.onTestClickHandler(test)}>
        <Row alignItems="center">
          <LXbody width={'64px'}>
            {getIndex(c.pageResult.pageNumber, c.pagination.pageSize, idx)}
          </LXbody>
          <SizedBox width={16}></SizedBox>
          <LXbody width={'120px'}>{test.serial}</LXbody>
          <SizedBox width={16}></SizedBox>
          <LXbody width={'312px'}>{buildTestName(test)}</LXbody>
          <SizedBox width={16}></SizedBox>
          <LXbody width={'176px'}>{test.clientName}</LXbody>
          <SizedBox width={16}></SizedBox>
          <LXbody width={'144px'}>{test.testStatus.label}</LXbody>
        </Row>
      </ItemPadding>
    </List.Item>
  );
  return (
    <XCenter>
      <Column>
        <SizedBox height={80}></SizedBox>
        {header}
        <SizedBox height={40}></SizedBox>
        {searchBox}
        <SizedBox height={20}></SizedBox>
        <Row justifyContent="end">
          <LXSortSelector
            value={c.pagination.isAscending ? 'Oldest' : 'Newest'}
            onSelect={c.setSortFilter}
          ></LXSortSelector>
        </Row>
        <SizedBox height={16}></SizedBox>
        <LXList<TestSummary>
          datas={c.tests}
          header={listHeader}
          itemBuilder={listItemBuilder}
          loading={c.isLoading}
        ></LXList>
        <SizedBox height={16}></SizedBox>
        {pagination}
      </Column>
    </XCenter>
  );
};

const TestListPageWrapper = () => {
  const testRepo = Get.find<ITestRepository>('ITestRepository');
  const testStatuses = wrapPromise<void, TestStatusDto[]>(
    testRepo.getTestStatuses,
    undefined,
    'testRepoKey'
  )();
  return <TestListPage testStatuses={testStatuses}></TestListPage>;
};

export default withAsync({
  child: TestListPageWrapper,
  suspenseFallback: <LXSpinner></LXSpinner>,
  errorFallback: <LXError></LXError>,
});
